/* eslint-disable react-hooks/exhaustive-deps */
import './index.less';

import {
  Box,
  Button,
  Checkbox,
  Flex,
  Icon,
  Link,
  Space,
  Stack,
  Switch,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { AddIcon, CloseSIcon } from '@tonic-one/react-icons';
import i18n from 'i18next';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConsoleType, SCHEDULE_FREQUENCY } from 'src/utils/enums';

import { ReportApi } from '@Apis';
import MenuWithLabel from '../../../components/Common/Menu';
import { useDisclosure } from '../../../components/Common/Tooltip/TextEllipsis/utils';
import { useUtcOffsetStore } from '../../../models/useUtcOffsetModel';
import ActionModal from '../ActionModal/common';
import DelModal from '../ActionModal/del';
import BottomPagination from '../BottomPagination';
import LoadingBtn from '../LoadingBtn';
import ReportTable from '../ReportTable';
import Searcher from '../Searcher';
import SmartTooltip from '../SmartTooltip';
import SortableHeader from '../SortableHeader';
import DataTrack from '../../../components/DataTrack';
import IconButton from 'src/components/IconButton';
import { ServiceNameEnum } from '../../../types';
import { SERVICE_NAME_ORDER_MAP } from '../../../utils/service';
import { useNavigateWithHandler } from '@Utils/hooks';
import ClassicScheduleReportDetailModal from './ClassicScheduleReportDetailModal';

export default function ScheduleReport(props: { withEditPermission: boolean }) {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  const { offset } = useUtcOffsetStore();
  // route
  const navigate = useNavigateWithHandler();
  // state
  const [tableData, setTableData] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [sortKey, setSortKey] = useState('name');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedNames, setSelectedNames] = useState<string[]>([]);
  const [delLoading, setDelLoading] = useState(false); // delete button loading
  const [tableLoading, setTableLoading] = useState(true);
  const [hasClassicScheduleReports, setHasClassicScheduleReports] =
    useState(false);
  // filter params
  const [filterParams, setFilterParams] = useState({
    page: 1,
    pageSize: 10,
    reportFrom: ConsoleType.NewConsole,
    frequency: '',
    format: '',
    content: '',
    template: '',
    input: '',
    trigger: 0, // operation trigger
  });
  // modal
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [delModalTitle, setDelModalTitle] = useState('Delete report');
  const [delModalContent, setDelModalContent] = useState('Delete report');
  const [curSchedule, setCurSchedule] = useState({
    id: '',
    enabled: false,
  });
  const {
    isOpen: isStatusOpen,
    onOpen: onStatusOpen,
    onClose: onStatusClose,
  } = useDisclosure();
  const [classicScheduleReportData, setClassicScheduleReportData] =
    useState<any>(null);

  const allSelected = useMemo(() => {
    if (!Array.isArray(tableData) || !Array.isArray(selectedIds)) return false;
    if (tableData.length === 0) return false;
    const ids: any[] = tableData.map((item: any) => item.id);
    for (const id of ids) {
      if (!selectedIds.includes(id)) return false;
    }
    return true;
  }, [tableData, selectedIds]);
  // table colums
  const columns = useMemo(
    () => [
      {
        id: 'selection',
        Header: () => {
          return (
            <Flex height="100%" alignItems="center">
              <Checkbox checked={allSelected} onChange={toggleSelectAll} />
            </Flex>
          );
        },
        accessor: (row: any) => {
          const checked = selectedIds.includes(row.id);
          return (
            <Flex height="100%" alignItems="center">
              <Checkbox
                checked={checked}
                onChange={() => {
                  toggleSelectItem(row.id);
                }}
              />
            </Flex>
          );
        },
        width: 48,
      },
      {
        id: 'name',
        noToolTip: true,
        Header: () => {
          return (
            <SortableHeader
              label={t('reportCommon.labels.name')}
              headerKey="name"
              currentSortKey={sortKey}
              changeSortKey={setSortKey}
            />
          );
        },
        accessor: (row: any) => {
          const gotoEdit = () => {
            const url = `/report/addSchedule/${row.id}`;
            navigate(url, { state: { id: row.id } });
          };
          return filterParams.reportFrom === ConsoleType.NewConsole ? (
            <Link width="100%" onClick={gotoEdit}>
              <SmartTooltip closeOnClick={true}>{row.name}</SmartTooltip>
            </Link>
          ) : (
            <Link
              width="100%"
              onClick={() => setClassicScheduleReportData(row)}
            >
              <SmartTooltip closeOnClick={true}>{row.name}</SmartTooltip>
            </Link>
          );
        },
        width: '12%',
      },
      {
        Header: t('reportCommon.labels.description'),
        accessor: 'description',
        width: '12%',
      },
      {
        id: 'orgName',
        Header: t('reportCommon.labels.organization'),
        accessor: (row: any): string => {
          if (row?.isGroup) {
            return t('common.label.allOrganizations');
          }
          return row?.orgName;
        },
        width: '16%',
      },
      {
        Header: t('reportCommon.labels.services'),
        accessor: ({ servicesTypes }: { servicesTypes: ServiceNameEnum[] }) => {
          const strArr: string[] = [];
          if (Array.isArray(servicesTypes)) {
            servicesTypes
              .sort((s1, s2) => {
                return SERVICE_NAME_ORDER_MAP[s1] - SERVICE_NAME_ORDER_MAP[s2];
              })
              .forEach((value) => {
                strArr.push(t(`common.service.${value}`));
              });
            const delimiter = i18n.language === 'ja' ? '、' : ', ';
            return strArr.join(delimiter);
          }
          return ' - ';
        },
        width: '18%',
      },
      {
        Header: t('reportCommon.labels.frequency'),
        // accessor: 'taskType',
        accessor: ({ frequency }: any) => {
          let label = '';
          SCHEDULE_FREQUENCY.map((item) => {
            if (frequency === item.key) {
              label = t(item.value);
            }
          });
          return label || '-';
        },
        width: '10%',
        minWidth: 120,
      },
      {
        Header: t('reportCommon.labels.format'),
        accessor: 'format',
        width: '8%',
        minWidth: 100,
      },
      {
        id: 'updatedAt',
        Header: () => {
          return (
            <SortableHeader
              label={t('reportCommon.labels.updated')}
              headerKey="updatedAt"
              currentSortKey={sortKey}
              changeSortKey={setSortKey}
            />
          );
        },
        accessor: (row: any) => {
          return row.updatedAt
            ? dayjs(row.updatedAt)
                .utcOffset(offset)
                .format(t('common.momentDateFormat'))
            : ' - ';
        },
        width: '15%',
      },
      {
        id: 'status',
        Header: t('reportCommon.labels.status'),
        accessor: (row: any) => {
          return (
            <Switch
              size="sm"
              width="auto"
              checked={row.enabledStatus}
              onChange={async () => {
                await switchStatus(row);
              }}
              disabled={
                !props.withEditPermission ||
                filterParams.reportFrom === ConsoleType.ClassicConsole
              }
            />
          );
        },
        width: '3%',
        minWidth: 100,
      },
    ],
    [allSelected, selectedIds, sortKey]
  );

  // table selection
  const toggleSelectAll = useCallback(() => {
    if (allSelected) {
      setSelectedIds([]);
      setSelectedNames([]);
    } else {
      // OK_InProgress
      const ids = Array.isArray(tableData)
        ? tableData.map((item: any) => item.id)
        : [];
      setSelectedIds(ids);
      const names = Array.isArray(tableData)
        ? tableData.map((item: any) => item.name)
        : [];
      setSelectedNames(names);
    }
  }, [allSelected, tableData]);

  const toggleSelectItem = useCallback(
    (id: string) => {
      if (selectedIds.includes(id)) {
        setSelectedIds((ids) => {
          return ids.filter((i) => i !== id);
        });
        setSelectedNames((names) => {
          return names.filter((name) => {
            return tableData.find((item) => item.id === id)?.name !== name;
          });
        });
      } else {
        setSelectedIds((ids) => {
          return [...ids, id];
        });
        setSelectedNames((names) => {
          return [
            ...names,
            tableData.find((item) => item.id === id)?.name || '',
          ];
        });
      }
    },
    [selectedIds]
  );

  // actions
  const changeCurrentPage = (page: any) => {
    setFilterParams({
      ...filterParams,
      page,
      trigger: new Date().valueOf(),
    });
  };

  const changePageSize = (pageSize: any) => {
    setFilterParams({
      ...filterParams,
      page: 1,
      pageSize,
      trigger: new Date().valueOf(),
    });
  };

  const handleDel = () => {
    // single
    if (selectedIds.length === 1) {
      setDelModalTitle(t('reportCommon.actions.delSchedule.title'));
      tableData.map((item: any) => {
        if (item.id === selectedIds[0]) {
          setDelModalContent(
            t('reportCommon.actions.delSchedule.desc', { name: item.name })
          );
        }
      });
    } else {
      // multiple
      setDelModalTitle(t('reportCommon.actions.delSchedules.title'));
      setDelModalContent(
        t('reportCommon.actions.delSchedules.desc', {
          count: selectedIds.length,
        })
      );
    }
    onOpen();
  };

  const confirmDel = async () => {
    setDelLoading(true);
    try {
      const res = await ReportApi.delSchedules(
        filterParams.reportFrom,
        selectedIds,
        selectedNames
      );
      if (res.data?.affected) {
        window.showNotification({
          titleMsg: t('reportCommon.alert.delSchedule.success'),
          severity: 'success',
        });
        await fetchData();
      }
    } catch (error: any) {
      window.showNotification({
        titleMsg: t('reportCommon.alert.delSchedule.fail'),
        bodyMsg: t('reportCommon.alert.delSchedule.failDesc'),
        severity: 'error',
      });
    } finally {
      onClose();
      setDelLoading(false);
    }
  };

  const resetDel = () => {
    setSelectedIds([]);
    setSelectedNames([]);
  };
  const switchStatus = async (row: any) => {
    setCurSchedule(row);
    if (row.enabledStatus) {
      // turn off need to confirm
      onStatusOpen();
      return;
    }
    await confirmDisable(row);
  };
  const confirmDisable = async (item?: any) => {
    if (!item) item = curSchedule;
    try {
      const res = await ReportApi.switchScheduleStatus(
        item?.id,
        item?.enabledStatus ? false : true
      );
      if (res.data?.affected === 1) {
        window.showNotification({
          titleMsg: t('reportCommon.alert.switchScheduleStatus.success'),
          severity: 'success',
        });
      }
    } catch (error: any) {
      window.showNotification({
        titleMsg: t('reportCommon.alert.switchScheduleStatus.fail'),
        bodyMsg: t('reportCommon.alert.switchScheduleStatus.failDesc'),
        severity: 'error',
      });
    } finally {
      onStatusClose();
      fetchData();
    }
  };
  // api - search
  const fetchData = async () => {
    setTableLoading(true);
    const { reportFrom, frequency, input, page, pageSize, template } =
      filterParams;
    // judge customer has classic schedule report or not
    const queryClassicParams = {
      reportFrom: ConsoleType.ClassicConsole,
      offset: 0,
      limit: 1,
      frequency: '',
      keyword: '',
      template: '', // TODO template filter
      sort: 'name',
    };
    const classicData = (await ReportApi.getSchedules(queryClassicParams)).data;
    if (Array.isArray(classicData?.list) && classicData?.list.length > 0) {
      setHasClassicScheduleReports(true);
    } else {
      setHasClassicScheduleReports(false);
    }
    // query data
    const params = {
      reportFrom,
      frequency,
      template, // TODO
      keyword: input,
      sort: sortKey,
      limit: pageSize,
      offset: pageSize * (page - 1),
    };
    const data = (await ReportApi.getSchedules(params)).data;
    if (Array.isArray(data?.list)) {
      setTableData(data.list);
      setTotalCount(data.count);
      setSelectedIds([]);
      setSelectedNames([]);
    }
    setTableLoading(false);
  };

  const gotoAdd = function () {
    const path = '/report/addSchedule';
    navigate(path);
  };

  useEffect(() => {
    fetchData();
  }, [filterParams?.trigger, sortKey]);

  const frequencyTypeMenuItems = useMemo(() => {
    return [
      {
        key: '',
        value: t('reportCommon.enums.scheduleFrequency.all'),
      },
      ...SCHEDULE_FREQUENCY.map((item) => {
        return { key: item.key, value: t(item.value) };
      }),
    ];
  }, []);

  const frequencyTypeMenuClick = (e: any) => {
    setFilterParams({
      ...filterParams,
      frequency: e.target.getAttribute('value'),
      page: 1,
      trigger: new Date().valueOf(),
    });
  };

  const reportFromMenuItems = [
    {
      key: ConsoleType.NewConsole,
      value: t('reportCommon.enums.reportFrom.newConsole'),
    },
    {
      key: ConsoleType.ClassicConsole,
      value: t('reportCommon.enums.reportFrom.classicConsole'),
    },
  ];

  const reportFromMenuClick = (e: any) => {
    setFilterParams({
      ...filterParams,
      reportFrom: e.target.getAttribute('value'),
      page: 1,
      trigger: new Date().valueOf(),
    });
  };

  return (
    <div>
      <div style={{ display: selectedIds.length > 0 ? 'none' : 'block' }}>
        <Stack direction="row" margin="4x">
          <DataTrack type="Report.NewSchedule.Add.Click">
            <Button
              variant="primary"
              onClick={() => gotoAdd()}
              disabled={filterParams.reportFrom === ConsoleType.ClassicConsole}
            >
              <Icon as={AddIcon} fontWeight="bold"></Icon>
              <Space width="2x" />
              {t('reportCommon.buttons.newSchedule')}
            </Button>
          </DataTrack>
          {hasClassicScheduleReports && (
            <div>
              <Space width="2x" />
              <MenuWithLabel
                minWidth={200}
                menuItems={reportFromMenuItems}
                selectedKey={filterParams?.reportFrom}
                onClick={reportFromMenuClick}
                label={t('reportCommon.labels.reportFrom')}
              />
            </div>
          )}
          <Space width="2x" />
          <MenuWithLabel
            minWidth={200}
            menuItems={frequencyTypeMenuItems}
            selectedKey={filterParams?.frequency}
            onClick={frequencyTypeMenuClick}
            label={t('reportCommon.labels.frequency')}
          />
          <Space width="2x" />
          <Searcher
            filterParams={filterParams}
            setFilterParams={setFilterParams}
            placeholder={t('reportCommon.labels.name')}
          />
        </Stack>
      </div>
      <div style={{ display: selectedIds.length > 0 ? 'block' : 'none' }}>
        <Stack direction="row" margin="4x">
          <LoadingBtn
            variant="secondary"
            mr="4x"
            onClick={handleDel}
            disabled={!props.withEditPermission}
          >
            {t('reportCommon.buttons.delete')}
          </LoadingBtn>
          <IconButton onClick={resetDel}>
            <Icon as={CloseSIcon}></Icon>
          </IconButton>
          <Box lineHeight="2rem">
            <Text color={colorStyle.color.tertiary}>
              {selectedIds.length}&nbsp;
              {t('reportCommon.placeholders.selected')}
            </Text>
          </Box>
        </Stack>
      </div>
      <ReportTable
        loading={tableLoading}
        columns={columns}
        data={tableData}
        tableStyle={{ width: '100%' }}
      />
      <BottomPagination
        totalCount={totalCount}
        currentPage={filterParams.page}
        pageSize={filterParams?.pageSize}
        changeCurrentPage={changeCurrentPage}
        changePageSize={changePageSize}
      />
      <DelModal
        visible={isOpen}
        onClose={onClose}
        onConfirm={confirmDel}
        loading={delLoading}
        title={delModalTitle}
        withEditPermission={props.withEditPermission}
      >
        <Text>{t('reportCommon.actions.delSchedules.preDesc')}</Text>
        <Text>{delModalContent}</Text>
      </DelModal>
      <ActionModal
        visible={isStatusOpen}
        onClose={onStatusClose}
        onConfirm={confirmDisable}
        loading={delLoading}
        title={t('reportCommon.actions.disableSchedule.title')}
        confirmText={t('reportCommon.buttons.disable')}
        cancelText={t('reportCommon.buttons.cancel')}
      >
        <Text>{t('reportCommon.actions.disableSchedule.desc')}</Text>
        <Text>{t('reportCommon.actions.disableSchedule.confirm')}</Text>
      </ActionModal>
      <ClassicScheduleReportDetailModal
        onClose={setClassicScheduleReportData}
        data={classicScheduleReportData}
      />
    </div>
  );
}
